import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNativePlatform, isNativePlatform } from '@/utils/capacitor';
import {
  canIosSwipeBack,
  getSwipeBackFallback,
  historyCanGoBack,
} from '@/utils/nativeNavigation';
import {
  IOS_SWIPE_BACK_EDGE_PX,
  setIosSwipeBackGestureActive,
  shouldGoBack,
} from '@/utils/iosSwipeBack';

export function useIosSwipeBack(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isNativePlatform() || getNativePlatform() !== 'ios') {
      return;
    }
    if (!canIosSwipeBack(location.pathname)) {
      return;
    }

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || touch.clientX > IOS_SWIPE_BACK_EDGE_PX) {
        tracking = false;
        return;
      }
      tracking = true;
      startX = touch.clientX;
      startY = touch.clientY;
      setIosSwipeBackGestureActive(true);
    };

    const onMove = (event: TouchEvent) => {
      if (!tracking) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const deltaX = touch.clientX - startX;
      const deltaY = Math.abs(touch.clientY - startY);
      if (deltaX > 12 && deltaX > deltaY) {
        event.preventDefault();
      }
    };

    const onEnd = (event: TouchEvent) => {
      if (!tracking) {
        return;
      }
      tracking = false;
      const touch = event.changedTouches[0];
      const deltaX = touch ? touch.clientX - startX : 0;
      const goBack = shouldGoBack({ startX, deltaX });
      setIosSwipeBackGestureActive(false);
      if (!goBack) {
        return;
      }
      if (historyCanGoBack()) {
        navigate(-1);
        return;
      }
      navigate(getSwipeBackFallback(location.pathname));
    };

    const onCancel = () => {
      tracking = false;
      setIosSwipeBackGestureActive(false);
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onCancel);

    return () => {
      setIosSwipeBackGestureActive(false);
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  }, [location.pathname, navigate]);
}
